"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { TIME_ZONE_COOKIE, deviceTimeZone } from "@/lib/time";

/** A year. The zone rarely changes, and when it does this corrects it on the
 *  next load rather than waiting for an expiry. */
const MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * Tells the server what time zone this device is set to.
 *
 * The server cannot know this. There is no request header for it — `Accept-Language`
 * carries a locale, not a zone, and IP geolocation is the same unreliable signal
 * the outline already rejected for the region gate. Only the browser knows, via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`.
 *
 * So: read it on the client, write it to a cookie, and let server components read
 * the cookie on every subsequent request. Why a cookie rather than formatting
 * dates in a client component:
 *
 *   - Formatting on the client means the server's HTML and the client's first
 *     render disagree, which is a hydration mismatch — the exact class of bug that
 *     cost real debugging time on this project twice already.
 *   - It would also force every date onto the client, or show a UTC flash that
 *     rewrites itself after hydration.
 *
 * With the cookie, pages stay server-rendered and every date is already correct in
 * the HTML. The cost is one extra render on a viewer's very first visit.
 *
 * Renders nothing.
 */
/** A stored value may be percent-encoded by an older build; decode before
 *  comparing so the check doesn't fail against its own past output. */
function readStoredZone(): string | undefined {
  const raw = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${TIME_ZONE_COOKIE}=`))
    ?.slice(TIME_ZONE_COOKIE.length + 1);
  if (!raw) return undefined;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function TimeZoneProbe() {
  const router = useRouter();

  useEffect(() => {
    const tz = deviceTimeZone();
    if (!tz) return;

    // Comparing against the stored value rather than tracking "have I run" in
    // state, because refresh() preserves client state — a flag in state would
    // survive but so would a stale cookie, and this has to notice a zone change.
    //
    // The comparison has to be like-for-like. An earlier version wrote the value
    // encodeURIComponent'd and compared it to the decoded zone, so they never
    // matched and refresh() fired on every single page load. That is invisible in
    // a document-request count, since refresh only re-fetches the RSC payload.
    if (readStoredZone() === tz) return;

    // Written raw. Every character an IANA id can contain is legal in a cookie
    // value, and writing it unencoded means the round trip does not depend on
    // whether the framework decodes on read — verified that Next does, but not
    // worth resting on.
    //
    // Client-written, so it cannot be httpOnly. Lax rather than Strict so it
    // survives arriving from an external link; a zone is not a credential and
    // carries nothing worth protecting from CSRF.
    document.cookie = `${TIME_ZONE_COOKIE}=${tz}; path=/; max-age=${MAX_AGE_SECONDS}; samesite=lax`;

    // Re-render on the server now that it can read the zone. The Next docs note
    // that request-time APIs like cookies() can change what refresh() produces —
    // which is precisely what is being relied on here.
    router.refresh();
  }, [router]);

  return null;
}
